import { useState, useEffect } from 'react';
import { motion } from 'motion/react';

function computeRemaining(targetDate) {
  if (!targetDate) return null;
  const diff = new Date(targetDate).getTime() - Date.now();
  if (diff <= 0) return null;
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);
  return { days, hours, minutes, seconds };
}

function TimerDigit({ value, label }) {
  return (
    <div className="flex flex-col items-center">
      <span className="text-2xl font-bold tabular-nums text-game-accent sm:text-3xl">{String(value).padStart(2, '0')}</span>
      <span className="mt-0.5 text-[9px] font-semibold uppercase tracking-widest text-game-muted">{label}</span>
    </div>
  );
}

const LEVEL_LABELS = {
  neutral: 'Neutral',
  trusting: 'Trusting',
  obedient: 'Obedient',
  devoted: 'Devoted'
};

export default function GameFusionModal({ fusionEventDate, fusionRequirements = [], charactersCatalog = [] }) {
  const [remaining, setRemaining] = useState(() => computeRemaining(fusionEventDate));

  useEffect(() => {
    if (!fusionEventDate) return undefined;
    const tick = () => setRemaining(computeRemaining(fusionEventDate));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [fusionEventDate]);

  return (
    <div className="space-y-6 p-6 text-center">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', damping: 20, stiffness: 260 }}
        className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-game-accent/20 text-3xl"
      >
        ✦
      </motion.div>

      <div>
        <h3 className="text-lg font-bold text-game-text">Fusion Room</h3>
        <p className="mt-2 text-sm leading-relaxed text-game-body">
          A place where characters interact and worlds collide.
          Stay tuned for the next cross-character event.
        </p>
      </div>

      {fusionRequirements.length > 0 ? (
        <div className="rounded-xl border border-white/10 bg-game-surface/60 p-4 text-left">
          <p className="mb-3 text-center text-xs font-semibold uppercase tracking-wide text-game-muted">
            Required relationship levels
          </p>
          <div className="space-y-2">
            {fusionRequirements.map((req, i) => {
              const char = charactersCatalog.find((c) => String(c._id) === String(req.characterId));
              const charName = char?.name || 'Unknown';
              const levelLabel = LEVEL_LABELS[req.relationshipLevel] || req.relationshipLevel;
              return (
                <div key={i} className="flex items-center justify-between gap-2 rounded-lg bg-game-canvas/60 px-3 py-2">
                  <span className="text-sm font-semibold text-game-text">{charName}</span>
                  <span className="rounded-md border border-game-accent/30 bg-game-accent/15 px-2 py-0.5 text-xs font-semibold text-game-accent">
                    {levelLabel}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {remaining ? (
        <div className="flex items-center justify-center gap-4">
          <TimerDigit value={remaining.days} label="Days" />
          <span className="text-xl font-bold text-game-muted/50">:</span>
          <TimerDigit value={remaining.hours} label="Hours" />
          <span className="text-xl font-bold text-game-muted/50">:</span>
          <TimerDigit value={remaining.minutes} label="Min" />
          <span className="text-xl font-bold text-game-muted/50">:</span>
          <TimerDigit value={remaining.seconds} label="Sec" />
        </div>
      ) : (
        <p className="text-base font-semibold text-game-accent">Coming Soon</p>
      )}
    </div>
  );
}
